import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { mdmLoader } from "src/common/mdmLoader";
import { rest } from "src/common/rest";

import {
  SpecificationType,
  ZSchemaLinks,
  zSchemaLinks,
  ZSecondarySchemaLinks,
  zSecondarySchemaLinks,
  ZSpecification,
  zSpecification,
  ZSpecRelations,
  zSpecRelations,
} from "./ZSpecification";
import { ZSpecificationCard } from "./ZSpecificationCard";

const urlSchemas = (path: string = "") => getApiUrl(`/core/schemas${path}`);

const urlSpec = (type: SpecificationType, path: string = "") =>
  urlSchemas(`${specUrlDict[type]}${path}`);

const specUrlDict: Record<SpecificationType, string> = {
  primary: "",
  secondary: "/secondary",
  values: "/values",
};

export const specLoader = (type: SpecificationType) =>
  mdmLoader(zSpecification, urlSpec(type));

export const loadSpecification = async (
  type: SpecificationType,
  id: string,
): Promise<ZSpecification> => {
  const resp = await rest.get(urlSpec(type, `/${id}`));
  return zSpecification.parse(resp.data);
};

export const saveSpecification = async (
  specType: SpecificationType,
  id: string,
  data: ZSpecificationCard,
): Promise<ZSpecification> => {
  const resp = await rest.put(urlSpec(specType, `/${id}`), data);
  return zSpecification.parse(resp.data);
};

export const createSpecification = async (
  specType: SpecificationType,
  data: ZSpecificationCard,
): Promise<ZSpecification> => {
  const resp = await rest.post(urlSpec(specType), data);
  return zSpecification.parse(resp.data);
};

export const deleteSpecifications = async (
  type: SpecificationType,
  ids: string[],
) => {
  await rest.delete(urlSpec(type), {
    data: { ids },
  });
};

export const testSpecRelations = async (
  id: string,
  type: "secondary" | "values",
): Promise<ZSpecRelations> => {
  const url = urlSpec(type, `/${id}/has-relations`);
  const resp = await rest.get(url);
  return dataParser(() => zSpecRelations.parse(resp.data), url);
};

export const loadSchemes = async (
  nodeId: string,
): Promise<ZSpecification[]> => {
  const resp = await rest.get(
    getApiUrl(`/core/hierarchies/${nodeId}/schemas/all`),
  );
  return dataParser(
    () => zSpecification.array().parse(resp.data),
    "loadSchemes",
  );
};

export const loadPrimarySpecForNode = async (
  nodeId: string,
): Promise<ZSpecification> => {
  const resp = await rest.get(getApiUrl(`/core/hierarchies/${nodeId}/schemas`));
  const specs = dataParser(
    () => zSpecification.array().parse(resp.data),
    "loadPrimarySpec",
  );
  const spec1 = specs[0];
  if (!spec1) throw Error("Отсутствует первичная спецификация");
  return spec1;
};

export const loadSchemaLinks = async (id: string): Promise<ZSchemaLinks> => {
  const resp = await rest.get(urlSchemas(`/${id}/links`));
  return dataParser(() => zSchemaLinks.parse(resp.data), "loadSchemaLinks");
};

export const loadSecondarySchemaLinks = async (
  id: string,
): Promise<ZSecondarySchemaLinks> => {
  const resp = await rest.get(urlSchemas(`/secondary/${id}/links`));
  return dataParser(
    () => zSecondarySchemaLinks.parse(resp.data),
    "loadSecondarySchemaLinks",
  );
};
